import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Upload, 
  Button, 
  Card, 
  Image, 
  Divider, 
  message,
  Alert,
  Progress,
  Tag
} from 'antd';
import { 
  DownloadOutlined, 
  FileImageOutlined,
  CompressOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { 
  compressImage,
  CompressionType,
  getAvailableCompressionTypes
} from '../services/imageService';
import { useLanguage } from '../i18n/LanguageContext';

const { Title, Paragraph } = Typography;
const { Dragger } = Upload;

// Color palette for different compression types
const colorPalette = [
  '#4caf50',  // Green
  '#2196f3',  // Blue
  '#ff9800',  // Orange
  '#9c27b0',  // Purple
  '#f44336',  // Red
  '#00bcd4',  // Cyan
  '#009688',  // Teal
  '#673ab7',  // Deep Purple
  '#3f51b5',  // Indigo
  '#8bc34a',  // Light Green
  '#cddc39',  // Lime
  '#ffeb3b',  // Yellow
  '#ffc107',  // Amber
  '#795548',  // Brown
  '#607d8b',  // Blue Grey
  '#e91e63',  // Pink
  '#ff5722',  // Deep Orange
];

const CompressPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  
  const [compressionType, setCompressionType] = useState<CompressionType>('png-compress');
  const [compressionInfo, setCompressionInfo] = useState<{
    name: string;
    description: string;
    acceptFormat: string;
    outputFormat: string;
  }>({
    name: 'PNG Compress',
    description: 'Reduce PNG file size while maintaining quality',
    acceptFormat: 'image/png',
    outputFormat: 'png'
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [compressedImage, setCompressedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageColor, setPageColor] = useState<string>('#4caf50');
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [compressedSize, setCompressedSize] = useState<number>(0);
  const [compressionProgress, setCompressionProgress] = useState<number>(0);
  const [compressing, setCompressing] = useState<boolean>(false);
  const [codecUsed, setCodecUsed] = useState<string>('');
  const { t } = useLanguage();

  useEffect(() => {
    if (type) {
      // Validate that this is a valid compression type
      const compressionTypes = getAvailableCompressionTypes(t);
      const compression = compressionTypes.find(c => c.type === type);
      
      if (compression) {
        setCompressionType(type as CompressionType);
        setCompressionInfo({
          name: compression.name,
          description: compression.description,
          acceptFormat: compression.acceptFormat,
          outputFormat: compression.outputFormat
        });
        
        // Set the codec used based on the compression type
        if (type === 'png-compress') {
          setCodecUsed('Squoosh-Browser');
        } else if (type === 'jpg-compress' || type === 'jpeg-compress') {
          setCodecUsed('Squoosh-Browser');
        }
        
        // Set a color based on the type
        const index = compressionTypes.findIndex(c => c.type === type);
        setPageColor(colorPalette[(index + 5) % colorPalette.length]);
      } else {
        // Invalid compression type - redirect to home
        navigate('/');
      }
    }
  }, [type, navigate]);

  const simulateCompressionProgress = () => {
    setCompressing(true);
    setCompressionProgress(0);
    
    const interval = setInterval(() => {
      setCompressionProgress(prev => {
        if (prev >= 99) {
          clearInterval(interval);
          return 99;
        }
        return prev + 5;
      });
    }, 100);
    
    return () => clearInterval(interval);
  };

  // Function to calculate the size of a data URL
  const getDataUrlFileSize = (dataUrl: string): number => {
    // Remove the metadata part of the data URL
    const base64String = dataUrl.split(',')[1];
    // Calculate the size: base64 is 4/3 the size of binary
    return Math.floor((base64String.length * 3) / 4);
  };

  const handleCompress = async () => {
    if (!imageFile) {
      message.error('Please upload an image first!');
      return;
    }

    setLoading(true);
    const stopProgressSimulation = simulateCompressionProgress();
    
    try {
      // Use default quality setting (80%)
      const result = await compressImage(imageFile, compressionType, 80);
      setCompressedImage(result);
      
      // Complete the progress bar
      setCompressionProgress(100);
      setTimeout(() => {
        setCompressing(false);
      }, 500);
      
      // Get the original file size
      const originalDataSize = imageFile.size;
      
      // Get the compressed file size from the data URL
      const actualCompressedSize = getDataUrlFileSize(result);
      
      setCompressedSize(actualCompressedSize);
      
      // Calculate the reduction percentage
      const reductionPercent = Math.max(0, Math.round(((originalDataSize - actualCompressedSize) / originalDataSize) * 100));
      
      message.success(`Successfully compressed image (${reductionPercent}% reduction)`);
      
      // Auto download the compressed image
      downloadCompressedImage(result);
    } catch (error) {
      message.error('Failed to compress image. Please try again.');
      console.error(error);
    } finally {
      stopProgressSimulation();
      setLoading(false);
    }
  };

  const downloadCompressedImage = (imageData: string) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = `compressed-image.${compressionInfo.outputFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: compressionInfo.acceptFormat,
    maxCount: 1,
    showUploadList: true,
    beforeUpload: (file) => {
      // Check if the file type matches the expected format
      if (!file.type.startsWith(compressionInfo.acceptFormat)) {
        message.error(`You can only upload ${compressionInfo.name.split(' ')[0]} files for compression!`);
        return Upload.LIST_IGNORE;
      }
      
      // Set the file for compression and prevent actual upload
      setImageFile(file);
      setOriginalSize(file.size);
      
      // Show a simple success message instead of a modal
      message.success(`${file.name} uploaded successfully. You can now compress it.`);
      
      return false;
    },
    onChange: (info) => {
      // This is still needed for interface updates
      const { status } = info.file;
      if (status === 'removed') {
        setImageFile(null);
        setCompressedImage(null);
        setOriginalSize(0);
        setCompressedSize(0);
      }
    },
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate compression percentage
  const getCompressionPercentage = (): number => {
    if (originalSize === 0 || compressedSize === 0) return 0;
    // Ensure the percentage is never negative
    return Math.max(0, Math.round(((originalSize - compressedSize) / originalSize) * 100));
  };

  return (
    <div className="container">
      <Typography>
        <Title style={{ color: pageColor }}>
          {compressionInfo.name} 
          {/* <Tag color={pageColor}>Powered by Squoosh Browser</Tag> */}
        </Title>
        <Paragraph>
          {t('compress.technology.description').replace('{description}', compressionInfo.description)}
        </Paragraph>
      </Typography>

      <Card 
        style={{ 
          borderTop: `2px solid ${pageColor}`,
          overflow: 'visible'
        }}
      >
        <Alert
          message={t('compress.tool.description').replace('{format}',compressionInfo.name.split(' ')[0])}
          description={t('compress.upload.description')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Title level={4} className="upload-title" style={{ color: pageColor }}>
          {t('compress.upload.title')}
        </Title>
        <div style={{ position: 'relative', marginTop: 24 }}>
          <Dragger 
            {...uploadProps} 
            className="upload-area"
            style={{ 
              borderColor: pageColor,
              borderWidth: '1px',
              borderStyle: 'dashed'
            }}
          >
            {imageFile ? (
              <div>
                <p className="ant-upload-drag-icon">
                  <CheckCircleOutlined style={{ color: pageColor, fontSize: 48 }} />
                </p>
                <p className="ant-upload-text" style={{ color: pageColor, fontWeight: 'bold' }}>
                  {t('compress.upload.complete').replace('{filename}', imageFile.name)}
                </p>
                <p className="ant-upload-hint">
                  {t('compress.original.size').replace('{size}', formatFileSize(originalSize))}
                </p>
              </div>
            ) : (
              <div>
                <p className="ant-upload-drag-icon">
                  <FileImageOutlined style={{ color: pageColor, fontSize: 48 }} />
                </p>
                <p className="ant-upload-text">{t('compress.upload.format.hint').replace('{format}', compressionInfo.name.split(' ')[0])}</p>
                <p className="ant-upload-hint">
                  {t('compress.upload.format.hint').replace('{format}', compressionInfo.name.split(' ')[0])}
                </p>
              </div>
            )}
          </Dragger>
        </div>
        
        {compressing && (
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <Paragraph>
              <strong>{t('compress.status')}</strong>
            </Paragraph>
            <Progress 
              percent={compressionProgress} 
              status={compressionProgress === 100 ? "success" : "active"} 
              strokeColor={pageColor} 
            />
          </div>
        )}
        
        <div className="button-group" style={{ display: 'flex', alignItems: 'center', marginTop: 24 }}>
          <Button 
            type="primary" 
            size="large" 
            onClick={handleCompress}
            loading={loading}
            disabled={!imageFile || compressing}
            icon={<CompressOutlined />}
            style={{ 
              backgroundColor: pageColor,
              borderColor: pageColor,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {t('compress.download.button')}
          </Button>

          <Button 
            size="large" 
            onClick={() => navigate('/')}
          >
            {t('compress.back.home')}
          </Button>
          
          {compressedImage && (
            <Button 
              type="default" 
              icon={<DownloadOutlined />} 
              onClick={() => downloadCompressedImage(compressedImage)}
            >
              {t('compress.download.again')}
            </Button>
          )}
        </div>
        
        {compressedImage && (
          <div style={{ marginTop: 24 }}>
            <Alert
              message={t('compress.complete')}
              description={
                <div>
                  <div>{t('compress.original.size').replace('{size}', formatFileSize(originalSize))}</div>
                  <div>{t('compress.compressed.size').replace('{size}', formatFileSize(compressedSize))}</div>
                  <div>{t('compress.reduction').replace('{percentage}', getCompressionPercentage().toString())}</div>
                  {/* <div style={{ marginTop: 8 }}>
                    <Tag color={pageColor}>Powered by Squoosh Browser</Tag>
                  </div> */}
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: 16, textAlign: 'left' }}
            />
            
            <Divider>{t('compress.comparison.title')}</Divider>
            
            <div className="comparison-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
              <div className="comparison-item" style={{ textAlign: 'center', flex: '1 1 45%', minWidth: '250px' }}>
                <Title level={5}>{t('compress.comparison.original')}</Title>
                {imageFile && (
                  <div>
                    <Image 
                      src={URL.createObjectURL(imageFile)} 
                      alt="Original" 
                      style={{ maxWidth: '100%', maxHeight: '200px', border: `1px solid #ccc` }}
                    />
                    <div>{formatFileSize(originalSize)}</div>
                  </div>
                )}
              </div>
              
              <div className="comparison-item" style={{ textAlign: 'center', flex: '1 1 45%', minWidth: '250px' }}>
                <Title level={5}>{t('compress.comparison.compressed')}</Title>
                <Image 
                  src={compressedImage} 
                  alt="Compressed" 
                  style={{ maxWidth: '100%', maxHeight: '200px', border: `1px solid ${pageColor}` }} 
                />
                <div>{formatFileSize(compressedSize)}</div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CompressPage; 